/* eslint-disable react/jsx-props-no-spreading */
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import LinkComponent from 'next/link';
import {
  createElement,
  Fragment,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
} from 'react';
import { v4 as uuid } from 'uuid';
import cookie from 'cookie';

import styles from 'src/styles/_commonClasses.module.scss';
import type {
  Block,
  Link,
  BlockContent,
  ImageWrapper,
} from 'src/__generated__/graphqlTypes';
import {
  CommonRichText,
  DataLink,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  WebpImage,
} from 'components/Shared/SharedComponents';
import { AboutNcCardClasses, UlListClasses } from 'types/classTypes';
import { Pair, RichTextParsersConfig } from 'types/objectTypes';
import {
  GAReplacementValue,
  Heading,
  ImageFitMode,
} from 'constants/enums';
import { CommonTitleInterface } from 'components/Shared/declarations';
import { replaceContentfulVariables } from 'lib/text.service';
import { getAppropriatePathForResource } from 'lib/tag.service';
import { BioOverlay } from 'src/shared/lib/bio-overlay';
import { useCustomRouter } from 'src/shared/lib/history/hooks';
import { SVG_TYPE, SVG_XML_TYPE } from 'constants/constants';

import { formatPhoneNumber } from '../shared/lib/phone';

export const mapMarksBold = (
  text: string,
  className?: string,
  data?: any,
) => (
  <strong
    key={text}
    className={className || styles.commonParagraphBold}
    {...data}
  >
    {text}
  </strong>
);

export const mapMarksBoldWithReplacement = (
  replacementObject: Record<string, string | undefined>,
) => (
  text: string | Array<string | boolean>,
  className?: string,
  data?: any,
) => {
  if (Array.isArray(text)) {
    return text.map((singleText) => {
      if (typeof singleText !== 'string') {
        return null;
      }

      const parsedText = replaceContentfulVariables(singleText, replacementObject);

      return mapMarksBold(parsedText, className, data);
    });
  }

  const parsedText = replaceContentfulVariables(text, replacementObject);

  return mapMarksBold(parsedText, className, data);
};

export const mapMarksBoldWithBr = (
  text: string,
  className?: string,
  data?: any,
) => (
  <Fragment key={text}>
    <br />
    <strong
      className={className || styles.commonParagraphBold}
      {...data}
    >
      {text}
    </strong>
  </Fragment>
);

export const mapMarksItalic = (
  text: string,
  className?: string,
  data?: any,
) => (
  <i
    key={text}
    title={text}
    className={className || styles.commonParagraphItalic}
    {...data}
  >
    {text}
  </i>
);

export const mapMarksUnderline = (
  text: string,
  className?: string,
  data?: any,
) => {
  const key = (typeof text === 'string') ? text : uuid();

  return (
    <u
      key={key}
      title={text}
      className={className || styles.commonParagraphUnderline}
      {...data}
    >
      {text}
    </u>
  );
};

export const mapMarksBoldSchema = (data: any) => (
  text: string,
  className?: string,
) => mapMarksBold(text, className, data);

export const mapBlocksEmbeddedAsset = (
  node: any,
  map: Map<string, any>,
  className?: string,
) => {
  const asset: any = map.get(node.data.target.sys.id);

  return (
    <WebpImage
      image={asset}
      key={node.data.target.sys.id}
      className={className}
    />
  );
};

export const mapBlocksHeroImage = (
  node: any,
  map: Map<string, any>,
  className?: string,
) => {
  const asset: any = map.get(node.data.target.sys.id);

  if (asset?.contentType === SVG_XML_TYPE || asset?.contentType === SVG_TYPE) {
    return (
      <WebpImage
        image="external"
        src={asset.url}
        className={className}
      />
    );
  }

  const getWidthLimit = (width: number) => ({
    width,
    media: `${width}px`,
    fit: ImageFitMode.SCALE,
  });
  const imageWidthLimit = [getWidthLimit(400), getWidthLimit(740)];

  return (
    <WebpImage
      image={asset}
      width="100%"
      height="100%"
      className={className}
      widthLimit={imageWidthLimit}
    />
  );
};

export const mapBlocksEmbeddedEntry = (
  node: any,
  map: Map<string, any>,
  className?: string,
) => {
  const entry: any = map.get(node.data.target.sys.id);

  return (
    <button
      key={node.data.target.sys.id}
      type="button"
      style={entry?.styles}
      className={className || styles.commonButton}
    >
      {entry?.text}
    </button>
  );
};

export const mapBlocksEmbeddedImageWrapper = (
  node: any,
  map: Map<string, any>,
  className?: string,
) => {
  const entry: ImageWrapper = map.get(node.data.target.sys.id);

  // @ts-ignore This part is not mapped on mapping form GraphQL
  if (!entry?.image?.fields?.file?.url) {
    return null;
  }

  // @ts-ignore
  const img = entry?.image?.fields?.file;
  const isSvg = img?.contentType?.includes('svg');

  if (isSvg) {
    return (
      <WebpImage
        image="external"
        height={entry.height!}
        width={entry.width!}
        src={img.url!}
        className={className}
      />
    );
  }

  return (
    <WebpImage
      image={img}
      className={className}
    />
  );
};

export const mapBlocksEmbeddedEntryLink = (id: string) => (
  node: any,
  map: Map<string, any>,
  className?: string,
) => {
  const link: Link = map.get(node.data.target.sys.id);

  if (!link) {
    return null;
  }

  return (
    <DataLink
      id={id}
      link={link}
      className={className}
    >
      {link.title}
    </DataLink>
  );
};

export const mapBlocksUlList = (
  node: any,
  className?: UlListClasses,
  data?: any,
) => {
  const listItems: Array<any> | undefined = node?.content;

  if (!listItems) return null;

  const listItemsRepresentation = listItems
    .map((listItem, i) => {
      const paragraphs: Array<any> | undefined = listItem?.content;

      if (!paragraphs) return null;

      const paragraphsRepresentation = paragraphs.map((paragraph, i) => {
        const textNodes: Array<any> | undefined = paragraph?.content;

        if (!textNodes) return null;

        const textNodesRepresentations = textNodes.map((textNode) => textNode.value);

        return (
          <p key={i.toString()} className={className?.p || styles.commonParagraph}>
            {textNodesRepresentations}
          </p>
        );
      });

      return (
        <li className={className?.li} key={i.toString()}>
          {paragraphsRepresentation}
        </li>
      );
    });

  return (
    <ul
      key={listItemsRepresentation.toString()}
      className={className?.ul}
      {...data}
    >
      {listItemsRepresentation}
    </ul>
  );
};

export const mapBlocksUlListWithReplacement = (
  replacementObject: Record<string, string | undefined>,
) => (
  node: any,
  className?: UlListClasses,
  data?: any,
) => {
  const listItems: Array<any> | undefined = node?.content;

  if (!listItems) return null;

  const listItemsRepresentation = listItems
    .map((listItem, i) => {
      const paragraphs: Array<any> | undefined = listItem?.content;

      if (!paragraphs) return null;

      const paragraphsRepresentation = paragraphs.map((paragraph, i) => {
        const textNodes: Array<any> | undefined = paragraph?.content;

        if (!textNodes) return null;

        const textNodesRepresentations = textNodes.map((textNode) => {
          if (typeof textNode.value === 'string') {
            return replaceContentfulVariables(textNode.value, replacementObject);
          }

          return textNode.value;
        });

        return (
          <p key={i.toString()} className={className?.p || styles.commonParagraph}>
            {textNodesRepresentations}
          </p>
        );
      });

      return (
        <li className={className?.li} key={i.toString()}>
          {paragraphsRepresentation}
        </li>
      );
    });

  return (
    <ul
      key={listItemsRepresentation.toString()}
      className={className?.ul}
      {...data}
    >
      {listItemsRepresentation}
    </ul>
  );
};

export const mapBlocksOlList = (
  node: any,
  className?: UlListClasses,
  data?: any,
) => {
  const listItems: Array<any> | undefined = node?.content;

  if (!listItems) return null;

  const listItemsRepresentation = listItems
    .map((listItem, i) => {
      const paragraphs: Array<any> | undefined = listItem?.content;

      if (!paragraphs) return null;

      const paragraphsRepresentation = paragraphs.map((paragraph, i) => {
        const textNodes: Array<any> | undefined = paragraph?.content;

        if (!textNodes) return null;

        const textNodesRepresentations = textNodes.map((textNode) => textNode.value);

        return (
          <p key={i.toString()} className={className?.p || styles.commonParagraph}>
            {textNodesRepresentations}
          </p>
        );
      });

      return (
        <li className={className?.li} key={i.toString()}>
          {paragraphsRepresentation}
        </li>
      );
    });

  return (
    <ol
      key={listItemsRepresentation.toString()}
      className={className?.ul}
      {...data}
    >
      {listItemsRepresentation}
    </ol>
  );
};

export const mapBlocksUlListSchema = (data: any) => (
  node: any,
  className?: UlListClasses,
) => mapBlocksUlList(node, className, data);

const getPChildKey = (child: string | object, i: number) => ((typeof child === 'string')
  ? `${child}${i}`
  : i.toString());

export const mapBlocksParagraph = (
  children: Array<string>,
  className?: string,
  data?: any,
) => (
  <p
    key={children[0]}
    className={className || styles.commonParagraph}
    title={children[0]}
    {...data}
  >
    {children.map((child, i) => (
      <Fragment key={getPChildKey(child, i)}>
        {child}
      </Fragment>
    ))}
  </p>
);

export const mapBlocksParagraphWithExtraData = (
  localData?: Record<string, any>,
) => (
  children: Array<string>,
  className?: string,
  data?: any,
) => mapBlocksParagraph(children, className, { ...data, ...localData });

export const mapBlocksParagraphWithReplacement = (
  replacementObject: Record<string, string | undefined>,
) => ((
  children: Array<string>,
  className?: string,
  data?: any,
) => (
  <p
    key={children[0]}
    className={className || styles.commonParagraph}
    {...data}
  >
    {children.map((child, i) => {
      if (typeof child === 'string') {
        const parsedText = replaceContentfulVariables(child, replacementObject);

        return (
          <Fragment key={getPChildKey(parsedText, i)}>
            {parsedText}
          </Fragment>
        );
      }

      return (
        <Fragment key={getPChildKey(child, i)}>
          {child}
        </Fragment>
      );
    })}
  </p>
));

export const mapBlocksParagraphWithModifiableText = (
  modifyText: (text: string) => string,
) => ((
  children: Array<string>,
  className?: string,
  data?: any,
) => children.map((child) => (
  <p
    key={child}
    className={className || styles.commonParagraph}
    {...data}
  >
    {modifyText(child)}
  </p>
)));

export const mapBlocksDiv = (
  children: Array<string>,
  className?: string,
  data?: any,
) => (
  <div
    key={children}
    className={className || styles.commonParagraph}
    {...data}
  >
    {children.map((child, i) => (
      <Fragment key={getPChildKey(child, i)}>
        {child}
      </Fragment>
    ))}
  </div>
);

export const mapBlocksParagraphSchemas = (data: any) => (
  children: any,
  className?: string,
) => mapBlocksParagraph(children, className, data);

export const mapBlocksSpan = (
  children: any,
  className?: string,
) => (
  <span key={children} className={className || styles.commonParagraph}>
    {children}
  </span>
);

export const mapBlocksSpanWithReplacement = (
  replacementObject: Record<string, string | undefined>,
) => (
  children: Array<string | any>,
  className?: string,
) => (
  <span key={children[0] || children[1]} className={className || styles.commonParagraph}>
    {children.map((child, i) => {
      if (typeof child === 'string') {
        const parsedText = replaceContentfulVariables(child, replacementObject);

        return (
          <Fragment key={getPChildKey(parsedText, i)}>
            {parsedText}
          </Fragment>
        );
      }

      if (!child) {
        return null;
      }

      return (
        <Fragment key={getPChildKey(child, i)}>
          {child}
        </Fragment>
      );
    })}
  </span>
);

export const mapTopBarPhone = (
  children: any,
  className?: string,
) => (
  <>
    {children.map((item: string) => (
      <span key={item} className={className || styles.commonParagraph}>
        {item}
      </span>
    ))}
  </>
);

export const mapBlocksEmbeddedEntryPrice = (
  node: any,
  map: Map<string, any>,
  classNames?: AboutNcCardClasses,
) => {
  const entry: Block = map.get(node.data.target.sys.id);
  const { content, title, link } = entry;
  const parsersConfig: RichTextParsersConfig = {
    [BLOCKS.PARAGRAPH]: {
      classNames: classNames?.contentParagraph || styles.commonParagraph,
    },
    [MARKS.BOLD]: {
      classNames: classNames?.contentBold || styles.commonParagraphBold,
      mapper: mapMarksBoldSchema({ itemProp: 'price' }),
    },
  };

  const existingClassNames = classNames || {} as AboutNcCardClasses;

  return (
    <DataLink
      link={link!}
      href={link?.src!}
      className={existingClassNames.wrapper}
    >
      <span className={existingClassNames.title || styles.commonTitle}>{title}</span>
      <CommonRichText content={content as BlockContent} parsersConfig={parsersConfig} />
    </DataLink>
  );
};

export const mapBlocksMainHeading = (
  children: Array<string>,
  className?: string,
) => (
  <H1 title={children} className={className} />
);

export const mapBlocksSecondHeading = (
  children: Array<string>,
  className?: string,
) => (
  <H2 title={children} className={className} />
);

export const mapBlocksSecondHeadingWithId = (id: string) => (
  children: Array<string>,
  className?: string,
) => (
  <H2
    title={children}
    className={className}
    id={id}
  />
);

export const mapBlocksThirdHeading = (
  children: Array<string>,
  className?: string,
) => (
  <H3 title={children} className={className} />
);

export const mapBlocksFourthHeading = (
  children: any,
  className?: string,
) => (
  <H4 title={children} className={className} />
);

export const mapBlocksFifthHeading = (
  children: any,
  className?: string,
) => (
  <H5 title={children} className={className} />
);

export const mapBlocksSixthHeading = (
  children: any,
  className?: string,
) => (
  <H6 title={children} className={className} />
);

export const mapBlocksHeadingWithId = (id: string = '', heading: Heading = Heading.SECOND) => {
  const headingsMap = {
    [Heading.FIRST]: H1,
    [Heading.SECOND]: H2,
    [Heading.THIRD]: H3,
    [Heading.FOURTH]: H4,
    [Heading.FIFTH]: H5,
    [Heading.SIXTH]: H6,
  };
  const Component = headingsMap[heading];

  return (
    children: Array<string>,
    className?: string,
  ) => (
    <Component
      title={children}
      className={className}
      id={id}
    />
  );
};

export const mapHyperlinkWithAdditionalProps = (
  additionalProps: HTMLAttributes<HTMLAnchorElement> = {},
) => (
  data: any,
  children: Array<string>,
  className?: string,
) => {
  const { isMatched, path } = getAppropriatePathForResource(data.uri);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    /** If bio overlay will be removed, then whole onClick should be removed too */
    BioOverlay.handleClickInIframe(e, path);
    additionalProps.onClick?.(e);
  };

  if (isMatched) {
    return (
      <a
        {...additionalProps}
        key={path}
        className={className}
        href={path}
        // eslint-disable-next-line react/jsx-props-no-multi-spaces
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <LinkComponent key={path} href={path}>
      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <a
        {...additionalProps}
        className={className}
        onClick={onClick}
      >
        {children}
      </a>
    </LinkComponent>
  );
};

export const mapHyperlink = (
  data: any,
  children: Array<string>,
  className?: string,
) => mapHyperlinkWithAdditionalProps({})(data, children, className);

const getHeadingComponent = (heading: Heading) => {
  const headingsMap = {
    [Heading.FIRST]: H1,
    [Heading.SECOND]: H2,
    [Heading.THIRD]: H3,
    [Heading.FOURTH]: H4,
    [Heading.FIFTH]: H5,
    [Heading.SIXTH]: H6,
  };
  const Component = headingsMap[heading];

  return Component;
};

export const mapBlocksHeadingWithReplacement = (
  replacementObject: Record<string, string | undefined>,
  heading: Heading = Heading.SECOND,
) => {
  const Component = getHeadingComponent(heading);

  return (
    children: Array<string>,
    className?: string,
  ) => {
    if (Array.isArray(children)) {
      const titles = children.map((text) => ((typeof text === 'string')
        ? replaceContentfulVariables(text, replacementObject)
        : text));

      return <Component title={titles} className={className} />;
    }

    const title = replaceContentfulVariables(children, replacementObject);

    return <Component title={title} className={className} />;
  };
};

export const mapBlocksHeadingWithParameters = (
  parameters: Partial<CommonTitleInterface>,
  heading: Heading = Heading.SECOND,
) => {
  const headingsMap = {
    [Heading.FIRST]: H1,
    [Heading.SECOND]: H2,
    [Heading.THIRD]: H3,
    [Heading.FOURTH]: H4,
    [Heading.FIFTH]: H5,
    [Heading.SIXTH]: H6,
  };
  const Component = headingsMap[heading];

  return (
    children: Array<string>,
    className?: string,
  ) => (
    <Component
      title={children}
      className={className}
      {... parameters}
    />
  );
};

export const mapHyperlinkToButtonWithCookie = (
  extId: string,
  cookieData: Array<Pair<string>>,
) => ((
  data: any,
  children: Array<string>,
  className?: string,
) => {
  const router = useCustomRouter();
  const url = `${data.uri}?extid=${extId}`;
  const onClick = () => {
    cookieData.forEach(({ key, value }) => {
      /* Wrap the value in double quotes if not already */
      const quotedValue = `"${value.replace(/"/g, '')}"`;
      const serializedCookie = cookie.serialize(key, quotedValue, {
        secure: process.env.NODE_ENV === 'production',
        path: '/',
      });
      document.cookie = serializedCookie;
    });

    router.push(url);
  };

  return (
    <button
      key={url}
      className={className}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  );
});

export const mapHyperlinkWithExtId = (
  extId: string,
) => (
  data: any,
  children: Array<string>,
  className?: string,
) => {
  const localData = extId ? { ...data, uri: `${data.uri}?extid=${extId}` } : data;

  return mapHyperlink(localData, children, className);
};

export const mapSmallText = (
  children: Array<string>,
  className?: string,
  data?: any,
) => (
  <small
    key={children.join()}
    className={className}
    {...data}
  >
    {children}
  </small>
);

export const mapHyperlinkWithGAData = (
  link?: Link,
  gaData?: Partial<Record<GAReplacementValue, string>>,
) => (
  data: any,
  children: Array<string>,
  className?: string,
) => {
  if (!link) {
    return mapHyperlink(data, children, className);
  }

  const localLink = { ...link, src: data.uri };

  return (
    <DataLink
      link={localLink}
      className={className}
      gaData={gaData}
    >
      {children}
    </DataLink>
  );
};

export const mapHyperlinkWithDynamicPhone = (object: Record<string, any>) => (
  data: any,
  children: Array<ReactElement | string | null>,
  className?: string,
) => {
  if (!object.telephone || !data.uri.startsWith('tel:')) {
    // @ts-ignore
    return mapHyperlink(data, children, className);
  }

  const modifyChild = (child: ReactElement | string | null) => {
    if (typeof child === 'string' || !child?.props?.children) {
      return object.telephone;
    }

    return createElement(
      child.type,
      { ...child.props, children: object.telephone, key: child.key },
    );
  };

  const modifyChildren = (children: Array<ReactElement | string | null>) => children
    .filter((child) => Boolean(child))
    .map(modifyChild);

  let newChildren = object.telephone;

  if (Array.isArray(children)) {
    if (!children.length) {
      newChildren = object.telephone;
    } else if (children.length === 1) {
      const innerChildren = children[0];

      if (Array.isArray(innerChildren)) {
        newChildren = modifyChildren(innerChildren);
      } else if (typeof innerChildren === 'object') {
        newChildren = modifyChild(innerChildren);
      }
    } else {
      newChildren = modifyChildren(children);
    }
  }

  return mapHyperlink({ ...data, uri: `tel:${object.telephone}` }, newChildren, className);
};

export const mapBlocksParagraphWithoutTitle = () => (
  children: string[],
  className?: string,
  data?: any,
) => {
  const newData = { ...data || {}, title: null };

  return mapBlocksParagraph(children, className, newData);
};

/** Not for reuse. If we need something for dynamic phones better to create dynamic links instead */
export const mapBlocksParagraphWithPhoneFormatting = (
  replacementObject: Record<string, string | undefined>,
) => ((children: Array<string>, className?: string, data?: any) => (
  <p key={children[0]} className={className || styles.commonParagraph} {...data}>
    {children.map((child, i) => {
      if (typeof child === 'string') {
        const replacedText = replaceContentfulVariables(child, replacementObject);

        return (
          <Fragment key={getPChildKey(child, i)}>
            {replacedText.split(replacementObject.telephone || '').map((part, index, arr) => (
              <Fragment key={getPChildKey(part, index)}>
                {part}
                {index < arr.length - 1 && replacementObject.telephone && (
                  <a className={styles.hyperlink} href={`tel:${replacementObject.telephone}`}>
                    <strong>{formatPhoneNumber(replacementObject.telephone)}</strong>
                  </a>
                )}
              </Fragment>
            ))}
          </Fragment>
        );
      }

      return <Fragment key={getPChildKey(String(child), i)}>{child}</Fragment>;
    })}
  </p>
));

export const mapBlocksSpanWithExtraElements = (elements: Array<ReactElement | string | null>) => (
  children: string[],
  className?: string,
) => (
  <>
    {mapBlocksSpan(children, className)}
    {elements}
  </>
);

export const mapBlocksHr = (
  node: any,
  className?: string,
) => (
  <hr
    key={node.toString()}
    className={className || styles.hr}
  />
);
